import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Modal from '../components/ui/Modal';

export interface ConfirmOptions {
  title?: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button in a destructive (red) style. */
  danger?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
  inputLabel?: string;
  /** Disallow submitting an empty value. */
  required?: boolean;
}

interface ConfirmContextValue {
  /** Resolves true if confirmed, false otherwise. Replaces window.confirm. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Resolves the entered string, or null if cancelled. Replaces window.prompt. */
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

type DialogState =
  | { kind: 'idle' }
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'prompt'; options: PromptOptions; resolve: (value: string | null) => void };

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState>({ kind: 'idle' });
  const [value, setValue] = useState('');
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setState({ kind: 'confirm', options, resolve })),
    [],
  );

  const prompt = useCallback((options: PromptOptions) => {
    setValue(options.defaultValue ?? '');
    return new Promise<string | null>((resolve) => setState({ kind: 'prompt', options, resolve }));
  }, []);

  const handleCancel = useCallback(() => {
    setState((prev) => {
      if (prev.kind === 'confirm') prev.resolve(false);
      else if (prev.kind === 'prompt') prev.resolve(null);
      return { kind: 'idle' };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((prev) => {
      if (prev.kind === 'confirm') {
        prev.resolve(true);
        return { kind: 'idle' };
      }
      if (prev.kind === 'prompt') {
        if (prev.options.required && !value.trim()) return prev; // keep open
        prev.resolve(value);
        return { kind: 'idle' };
      }
      return prev;
    });
  }, [value]);

  const isPrompt = state.kind === 'prompt';
  const options = state.kind === 'idle' ? undefined : state.options;
  const promptOptions = state.kind === 'prompt' ? state.options : undefined;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Modal
        isOpen={state.kind !== 'idle'}
        onClose={handleCancel}
        title={options?.title ?? (isPrompt ? 'Enter a value' : 'Are you sure?')}
        size="sm"
        initialFocusRef={isPrompt ? inputRef : confirmBtnRef}
        footer={
          <>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              {options?.cancelLabel ?? 'Cancel'}
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              onClick={handleConfirm}
              className={`rounded-xl px-4 py-2 text-sm font-medium text-white ${
                options?.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {options?.confirmLabel ?? (isPrompt ? 'Submit' : 'Confirm')}
            </button>
          </>
        }
      >
        {options?.message && <p className="mb-3">{options.message}</p>}
        {promptOptions && (
          <div>
            {promptOptions.inputLabel && (
              <label
                htmlFor="confirm-prompt-input"
                className="mb-1 block text-xs font-medium text-gray-500 dark:text-slate-400"
              >
                {promptOptions.inputLabel}
              </label>
            )}
            <input
              id="confirm-prompt-input"
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
              placeholder={promptOptions.placeholder}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            />
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
};
